import { Schema } from "effect";

/**
 * Kubernetes resource identifiers - branded types for type safety.
 *
 * These are K8s resource names (strings), NOT database entity IDs.
 * Use these to prevent accidentally mixing up cluster names, namespaces, etc.
 */

/**
 * Kubernetes cluster name (e.g., "aks-prod-westeurope-01", "bip-02")
 */
export const ClusterName = Schema.String.pipe(
  Schema.brand("ClusterName")
);
export type ClusterName = typeof ClusterName.Type;

/**
 * Kubernetes namespace name (e.g., "default", "kube-system", "my-app")
 */
export const NamespaceName = Schema.String.pipe(
  Schema.brand("NamespaceName")
);
export type NamespaceName = typeof NamespaceName.Type;

/**
 * Kubernetes controller name - the workload identifier
 * (e.g., "api-server-787cf77cdc", "nginx-ingress-controller")
 */
export const ControllerName = Schema.String.pipe(
  Schema.brand("ControllerName")
);
export type ControllerName = typeof ControllerName.Type;

/**
 * Kubernetes pod name (e.g., "api-server-787cf77cdc-x9k2p")
 */
export const PodName = Schema.String.pipe(
  Schema.brand("PodName")
);
export type PodName = typeof PodName.Type;

/**
 * Kubernetes node name (e.g., "aks-nodepool1-12345678-vmss000000")
 */
export const NodeName = Schema.String.pipe(
  Schema.brand("NodeName")
);
export type NodeName = typeof NodeName.Type;

/**
 * Kubernetes controller kind (e.g., "Deployment", "StatefulSet", "DaemonSet", "ReplicaSet", "Job", "CronJob")
 */
export const ControllerKind = Schema.String.pipe(
  Schema.brand("ControllerKind")
);
export type ControllerKind = typeof ControllerKind.Type;

/**
 * Kubernetes kube-system namespace UID - used as stable cluster identifier
 * (e.g., "647e9b2f-95fa-4544-a779-e5d8bd77a63d")
 */
export const KubeSystemUid = Schema.String.pipe(
  Schema.brand("KubeSystemUid")
);
export type KubeSystemUid = typeof KubeSystemUid.Type;

/**
 * Known Kubernetes pod phases - high-level lifecycle state
 * Note: K8s can return other values, so type is string for flexibility
 * @see https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-phase
 */
export const KnownPodPhases = [
  "Failed",
  "Pending",
  "Running",
  "Succeeded",
  "Unknown",
] as const;
export type KnownPodPhase = (typeof KnownPodPhases)[number];

/**
 * Known Kubernetes pod/container statuses - more detailed status information
 * Note: K8s can return other values, so type is string for flexibility
 */
export const KnownPodStatuses = [
  "Completed",
  "ContainerCreating",
  "CrashLoopBackOff",
  "CreateContainerConfigError",
  "Error",
  "ErrImagePull",
  "ImagePullBackOff",
  "Init:CrashLoopBackOff",
  "Init:Error",
  "InvalidImageName",
  "OOMKilled",
  "PodInitializing",
  "Running",
  "Terminated",
  "Terminating",
  "Unknown",
  "Waiting",
] as const;
export type KnownPodStatus =
  (typeof KnownPodStatuses)[number];
